import { useState } from "react";
import { RefreshCw } from "lucide-react";
import type { Product, ProductVariant } from "@/types";
import { Container, Button } from "@/components/atoms";
import { formatPrice } from "@/lib/format";
import { subscribePrice } from "@/lib/product-helpers";
import { cn } from "@/lib/cn";
import { useSiteSettings } from "@/hooks/use-site-settings";

const frequencies = [
  { days: 30, label: "Every 30 days" },
  { days: 60, label: "Every 60 days" },
  { days: 90, label: "Every 90 days" },
];

export function SubscriptionBlock({
  product,
  variant,
  onStart,
}: {
  product: Product;
  variant: ProductVariant;
  onStart: (frequencyDays: number) => void;
}) {
  // DRB-38: Subscribe & Save is off. This block promised 'Every 30 days' and
  // 'skip, pause or cancel anytime' with no recurring billing behind it (DRB-35).
  //
  // EVERY hook must run before the early return. DRB-38 inserted the `return null` ABOVE
  // `useState`, which is a react-hooks/rules-of-hooks error, and `npm run lint` is the FIRST
  // command in main.yml's combined run-block — bash `-e` aborts the step on it, so typecheck,
  // test, build, deploy and the post-deploy hash check never executed. That is the whole reason
  // CI has failed 37 times out of 37 since 2026-08-13 and every release since has been a manual
  // deploy past a red pipeline. Order matters here; do not move the return back up.
  const { settings } = useSiteSettings();
  const [freq, setFreq] = useState(30);

  if (settings.subscriptionsEnabled !== true) return null;

  const price = subscribePrice(product, variant);

  return (
    <section className="bg-leaf-green-2/15 py-12">
      <Container>
        <div className="mx-auto flex max-w-2xl flex-col gap-5 rounded-3xl bg-white p-8 text-center">
          <RefreshCw size={32} className="mx-auto text-leaf-green-ink" />
          <h2 className="font-condensed text-2xl font-bold text-night-blue">
            Subscribe &amp; Save 15%
          </h2>
          <p className="text-night-blue/70">
            Free shipping on every delivery. Skip, pause, or cancel anytime.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {frequencies.map((f) => (
              <button
                key={f.days}
                type="button"
                onClick={() => setFreq(f.days)}
                className={cn(
                  "rounded-pill border-2 px-4 py-2 text-sm font-bold transition-colors",
                  freq === f.days
                    ? "border-leaf-green bg-leaf-green/10 text-night-blue"
                    : "border-night-blue/15 text-night-blue/70",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <p className="font-condensed text-xl font-bold text-leaf-green-ink sm:text-2xl">
            {formatPrice(price)}
            <span className="ml-1 text-sm font-normal text-night-blue/70">
              / shipment
            </span>
          </p>
          <Button onClick={() => onStart(freq)} size="lg" className="mx-auto">
            Start Subscription
          </Button>
        </div>
      </Container>
    </section>
  );
}
